import { useApiClient } from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

interface GenerateTourTranslationsParams {
  tourId: string;
  targetLanguageIds: string[];
}

interface GenerateTourTranslationsResponse {
  success: boolean;
  message?: string;
  error?: string;
  data?: {
    created: string[];
    skipped: Array<{
      languageId: string;
      reason: string;
    }>;
    sourceTour: {
      id: string;
    };
  };
}

/**
 * Hook for generating tour translations to other languages
 */
export function useGenerateTourTranslations() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tourId,
      targetLanguageIds,
    }: GenerateTourTranslationsParams) => {
      const response = (await apiClient.post(`/tours/${tourId}/generate-translations`, {
        targetLanguageIds,
      })) as GenerateTourTranslationsResponse;

      if (!response || response.success === false) {
        throw new Error(response?.error || 'No response data');
      }
      return response;
    },
    onSuccess: (data) => {
      if (data.success && data.data) {
        // Invalidate translation queries to refresh the list
        queryClient.invalidateQueries({ queryKey: ['Translation'] });

        const createdCount = data.data.created.length;
        const skippedCount = data.data.skipped.length;

        if (createdCount > 0) {
          toast.success(
            `Created ${createdCount} translation${createdCount > 1 ? 's' : ''}${
              skippedCount > 0 ? `. Skipped ${skippedCount} (already exist)` : ''
            }`,
          );
        } else if (skippedCount > 0) {
          toast.info(`All selected languages already have translations`);
        }
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to generate translations');
    },
  });
}
